const Mercator_Origin_Shift = 20037508.342789244;

export type Wgs84_Point = { lat: number; lon: number };

export type Map_Bounds = {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
};

export function Coords_Look_Like_Mercator(Lon: number, Lat: number): boolean {
  return Math.abs(Lon) > 180 || Math.abs(Lat) > 90;
}

export function Mercator_To_Wgs84(X: number, Y: number): Wgs84_Point {
  const Lon = (X / Mercator_Origin_Shift) * 180;
  const Lat =
    (Math.atan(Math.exp((Y / Mercator_Origin_Shift) * Math.PI)) * 360) / Math.PI - 90;
  return { lat: Lat, lon: Lon };
}

export function Ring_To_Wgs84(Rings: number[][][]): number[][] {
  const Ring = Rings[0];
  if (!Ring?.length) {
    return [];
  }
  const Uses_Mercator = Coords_Look_Like_Mercator(Ring[0][0], Ring[0][1]);
  return Ring.map(([X, Y]) => {
    if (Uses_Mercator) {
      const Wgs = Mercator_To_Wgs84(X, Y);
      return [Wgs.lon, Wgs.lat];
    }
    return [X, Y];
  });
}

export function Parcel_Wgs84_Centroid(
  Latitude: number,
  Longitude: number,
  Ring_Wgs84: number[][],
): Wgs84_Point {
  if (Coords_Look_Like_Mercator(Longitude, Latitude)) {
    return Mercator_To_Wgs84(Longitude, Latitude);
  }
  if (Ring_Wgs84.length) {
    const Lats = Ring_Wgs84.map(([, Lat]) => Lat);
    const Lons = Ring_Wgs84.map(([Lon]) => Lon);
    return {
      lat: Lats.reduce((Sum, Value) => Sum + Value, 0) / Lats.length,
      lon: Lons.reduce((Sum, Value) => Sum + Value, 0) / Lons.length,
    };
  }
  return { lat: Latitude, lon: Longitude };
}

function Lat_To_Rad(Lat: number): number {
  return (Lat * Math.PI) / 180;
}

export function Bounds_From_Ring(Ring_Wgs84: number[][], Center: Wgs84_Point): Map_Bounds {
  if (!Ring_Wgs84.length) {
    const Pad = 0.001;
    return {
      minLat: Center.lat - Pad,
      maxLat: Center.lat + Pad,
      minLon: Center.lon - Pad,
      maxLon: Center.lon + Pad,
    };
  }

  let Min_Lat = Infinity;
  let Max_Lat = -Infinity;
  let Min_Lon = Infinity;
  let Max_Lon = -Infinity;
  for (const [Lon, Lat] of Ring_Wgs84) {
    Min_Lat = Math.min(Min_Lat, Lat);
    Max_Lat = Math.max(Max_Lat, Lat);
    Min_Lon = Math.min(Min_Lon, Lon);
    Max_Lon = Math.max(Max_Lon, Lon);
  }
  return { minLat: Min_Lat, maxLat: Max_Lat, minLon: Min_Lon, maxLon: Max_Lon };
}

export function Zoom_For_Bounds(Bounds: Map_Bounds, Width: number, Height: number): number {
  const World_Dim = 256;
  const Zoom_Max = 18;
  const Lat_Fraction =
    (Lat_To_Rad(Bounds.maxLat) - Lat_To_Rad(Bounds.minLat)) / Math.PI;
  const Lon_Fraction = (Bounds.maxLon - Bounds.minLon) / 360;
  const Lat_Zoom = Math.floor(
    Math.log(Height / World_Dim / Math.max(Lat_Fraction, 0.00001)) / Math.LN2,
  );
  const Lon_Zoom = Math.floor(
    Math.log(Width / World_Dim / Math.max(Lon_Fraction, 0.00001)) / Math.LN2,
  );
  return Math.max(12, Math.min(Lat_Zoom, Lon_Zoom, Zoom_Max));
}

export function LonLat_To_Pixel(Lon: number, Lat: number, Zoom: number): { x: number; y: number } {
  const Sin_Lat = Math.sin(Lat_To_Rad(Lat));
  const Scale = 2 ** Zoom;
  const X = ((Lon + 180) / 360) * Scale * 256;
  const Y = (0.5 - Math.log((1 + Sin_Lat) / (1 - Sin_Lat)) / (4 * Math.PI)) * Scale * 256;
  return { x: X, y: Y };
}

export function LonLat_To_Svg_Point(
  Lon: number,
  Lat: number,
  Center: Wgs84_Point,
  Zoom: number,
  Width: number,
  Height: number,
): { x: number; y: number } {
  const Center_Px = LonLat_To_Pixel(Center.lon, Center.lat, Zoom);
  const Point_Px = LonLat_To_Pixel(Lon, Lat, Zoom);
  return {
    x: Width / 2 + (Point_Px.x - Center_Px.x),
    y: Height / 2 + (Point_Px.y - Center_Px.y),
  };
}

export function Ring_To_Svg_Points(
  Ring_Wgs84: number[][],
  Center: Wgs84_Point,
  Zoom: number,
  Width: number,
  Height: number,
): string {
  if (!Ring_Wgs84.length) {
    return "";
  }
  return Ring_Wgs84
    .map(([Lon, Lat]) => {
      const Point = LonLat_To_Svg_Point(Lon, Lat, Center, Zoom, Width, Height);
      return `${Point.x.toFixed(1)},${Point.y.toFixed(1)}`;
    })
    .join(" ");
}

export function Tiles_For_Viewport(
  Center: Wgs84_Point,
  Zoom: number,
  Width: number,
  Height: number,
): Array<{ x: number; y: number; left: number; top: number }> {
  const Center_Px = LonLat_To_Pixel(Center.lon, Center.lat, Zoom);
  const Left = Center_Px.x - Width / 2;
  const Top = Center_Px.y - Height / 2;
  const Right = Left + Width;
  const Bottom = Top + Height;

  const Tile_Min_X = Math.floor(Left / 256);
  const Tile_Max_X = Math.floor((Right - 1) / 256);
  const Tile_Min_Y = Math.floor(Top / 256);
  const Tile_Max_Y = Math.floor((Bottom - 1) / 256);

  const Tiles: Array<{ x: number; y: number; left: number; top: number }> = [];
  for (let Tile_Y = Tile_Min_Y; Tile_Y <= Tile_Max_Y; Tile_Y++) {
    for (let Tile_X = Tile_Min_X; Tile_X <= Tile_Max_X; Tile_X++) {
      Tiles.push({
        x: Tile_X,
        y: Tile_Y,
        left: Tile_X * 256 - Left,
        top: Tile_Y * 256 - Top,
      });
    }
  }
  return Tiles;
}

export function Format_Latitude(Lat: number): string {
  const Hem = Lat >= 0 ? "N" : "S";
  return `${Math.abs(Lat).toFixed(4)}°${Hem}`;
}

export function Format_Longitude(Lon: number): string {
  const Hem = Lon >= 0 ? "E" : "W";
  return `${Math.abs(Lon).toFixed(4)}°${Hem}`;
}
